import { Activity, Database, Zap, ShieldCheck, AlertCircle, Clock } from 'lucide-react';
import AdminPageHeader from './AdminPageHeader';
import AdminChartCard from './AdminChartCard';

interface AdminHealthProps {
  systemErrors: any[];
}

const sysMetrics = [
  { label: 'Edge Functions', status: 'Healthy', uptime: '99.99%', icon: Zap },
  { label: 'Postgres Database', status: 'Healthy', uptime: '100%', icon: Database },
  { label: 'Redis Cache', status: 'Active', uptime: '99.95%', icon: Activity },
  { label: 'Real-time Gateway', status: 'Active', uptime: '99.98%', icon: ShieldCheck },
];

const AdminHealth = ({ systemErrors }: AdminHealthProps) => {
  return (
    <div className="animate-in fade-in duration-500 space-y-6">
      <div className="mb-6">
        <AdminPageHeader 
          title="System Health"
          description="Monitor infrastructure status and global application stability."
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Operational Status */}
        <AdminChartCard 
          title="Operational Status" 
          subtitle="Real-time infrastructure telemetry"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full content-start">
            {sysMetrics.map((sys, i) => (
              <div key={i} className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-md bg-background shadow-sm border border-border/50">
                    <sys.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{sys.label}</p>
                    <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mt-0.5">
                      {sys.uptime} Uptime
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  {sys.status}
                </span>
              </div>
            ))}
          </div>
        </AdminChartCard>

        {/* Recent Failures */}
        <AdminChartCard 
          title="Recent Failures & Exceptions" 
          subtitle="Dead letter transactions and system faults"
          className="border-destructive/20"
          action={
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-destructive/10 text-destructive border border-destructive/20">
              {systemErrors.length} Alerts
            </span>
          }
        >
          <div className="flex flex-col gap-3 h-full">
            {systemErrors.length > 0 ? (
              <div className="space-y-3">
                {systemErrors.map((err, i) => (
                  <div key={i} className="p-4 rounded-lg border border-border flex items-center justify-between group hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                        <Clock className="h-5 w-5 text-destructive" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{err.reason}</p>
                        <p className="text-xs text-muted-foreground font-mono mt-1">
                          {new Date(err.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground ml-4 shrink-0 px-2 py-1 rounded bg-muted">
                      ID: {err.id?.slice(0, 8)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                <ShieldCheck className="h-12 w-12 text-emerald-500/40 mb-4" />
                <p className="text-sm font-medium">All systems operational</p>
                <p className="text-xs mt-1">No recent faults detected in the telemetry stream.</p>
              </div>
            )}
          </div>
        </AdminChartCard>
      </div>
    </div>
  );
};

export default AdminHealth;
